// src/components/settings/shared/dialogs/PetColourDialog.tsx

import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import DialogBase from '../../../common/modal/DialogBase';
import {
  StyledTextField,
  StyledSwitch,
  DailogLabel,
  DialogLabelText,
} from '../../../forms/shared/FormElements';
import FieldHelperText from '../../../forms/shared/FieldHelperText';
import { ModalCancelButton, ModalSaveButton } from '../../../common/Buttons';
import CustomSelect from '../../../common/CustomSelect';
import CustomSelectMenuItem from '../../../common/CustomSelectMenuItem';
import { PetColour, PetType } from '../../../../services/api';

interface PetColourFormData {
  colour: string;
  petTypeId: number;
  active: boolean;
}

interface PetColourDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: PetColourFormData) => void;
  editing: PetColour | null;
  petTypes: PetType[];
}

const PetColourDialog: React.FC<PetColourDialogProps> = ({
  open,
  onClose,
  onSave,
  editing,
  petTypes,
}) => {
  const [formData, setFormData] = useState<PetColourFormData>({
    colour: '',
    petTypeId: 0,
    active: true,
  });

  useEffect(() => {
    if (open) {
      setFormData(
        editing
          ? {
              colour: editing.colour,
              petTypeId: editing.petType ?? 0,
              active: editing.active ?? true,
            }
          : { colour: '', petTypeId: 0, active: true }
      );
    }
  }, [open, editing]);

  const canSave = formData.colour.trim().length > 0 && formData.petTypeId > 0;
  const isSystem = !!editing?.isSystem;

  return (
    <DialogBase
      open={open}
      onClose={onClose}
      title={editing ? 'Edit Pet Colour' : 'Add Pet Colour'}
      headerIcon={editing ? <EditIcon /> : <AddIcon />}
      draggable
      footer={
        <>
          <ModalCancelButton
            onClick={onClose}
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
            }}
          />
          <ModalSaveButton
            onClick={() => onSave(formData)}
            disabled={!canSave}
            text="Save"
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
            }}
          />
        </>
      }
    >
      <Box display="flex" flexDirection="column" gap={2} mt={2}>
        <DailogLabel>
          <DialogLabelText id="pet-colour-name-label">
            Colour Name:
          </DialogLabelText>
        </DailogLabel>
        <StyledTextField
          id="pet-colour-name-input"
          inputProps={{ 'aria-labelledby': 'pet-colour-name-label' }}
          value={formData.colour}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData({ ...formData, colour: e.target.value })
          }
          required
          fullWidth
          disabled={isSystem}
        />
        <DailogLabel>
          <DialogLabelText>Pet Type:</DialogLabelText>
        </DailogLabel>
        <CustomSelect
          value={formData.petTypeId ? String(formData.petTypeId) : ''}
          onChange={(value) =>
            setFormData({ ...formData, petTypeId: Number(value) })
          }
          ariaLabel="Pet Type"
          required
          disabled={isSystem}
        >
          {petTypes.map((pt) => (
            <CustomSelectMenuItem key={pt.id} value={String(pt.id)}>
              {pt.type}
            </CustomSelectMenuItem>
          ))}
        </CustomSelect>
        {isSystem && (
          <FieldHelperText helperText="Standard system item - name and pet type cannot be changed. You can still deactivate it below." />
        )}
        {editing && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography sx={{ fontSize: '1.8rem', fontWeight: 600 }}>
              Active
            </Typography>
            <StyledSwitch
              checked={formData.active}
              onChange={(e) =>
                setFormData({ ...formData, active: e.target.checked })
              }
            />
          </Box>
        )}
      </Box>
    </DialogBase>
  );
};

export default PetColourDialog;
